import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useGetVarantFromProduct } from "@babackapp/private-api-client/client/use-get-variants-from-product";
import { ProductVariantDto } from "@babackapp/private-api-client/types";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import { Loader2 } from "lucide-react";
import { PropsWithChildren } from "react";
import { useCurrentReturnRequest } from "../../utils/current-return-request-context";
import useTranslation from "@babackapp/translations/use-translation";

type VariantSelectProps = {
  productId: string;
  onChange?: (variant: ProductVariantDto) => void;
  editable?: boolean;
  lineItemId: string;
};

export default function VariantSelect({
  productId,
  onChange,
  children,
  editable,
  lineItemId,
}: PropsWithChildren<VariantSelectProps>) {
  const { data: shop } = useCurrentShopSuspense();
  const t = useTranslation();
  const currentReturnRequest = useCurrentReturnRequest();
  const minQuantity = shop.exchange.minimalStock || 0;

  const { data: product } = useGetVarantFromProduct({
    shopId: shop.id,
    productId: productId,
    returnRequestId: currentReturnRequest.id,
    lineItemId: lineItemId,
  });

  /**
   * Displays a loader while the product variants are being fetched
   */
  if (!editable) return children;
  if (!product) return <Loader2 className="size-2 animate-spin" />;

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger>{children}</DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56">
        <DropdownMenuLabel>
          {t("returnDetail.confirmModal.chooseVariant")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {product?.variants.map((variant) => (
          <DropdownMenuItem
            key={variant.id}
            onSelect={() => onChange?.(variant)}
            data-cy="select-resolution-option-exchange"
            disabled={variant.inventory_quantity < minQuantity}
          >
            {variant.title}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
